// import { debounce } from 'lodash';
import {
  useEffect, useMemo, useReducer, useState,
} from 'react';
import {
  areAllColumnsSelected as ucAreAllColumnsSelected,
  isAllColumnsSelectionIndeterminate as ucIsAllColumnsSelectionIndeterminate,
  isColumnsGroupSelectionIndeterminate as ucIsColumnsGroupSelectionIndeterminate,
  getAllColumnIds,
  tableColumnsCount as ucTableColumnsCount,
  searchInSearchOptions as ucSearchInSearchOptions,
  convertArrayToObject,
  convertObjectToArray,
} from './tableSearchUseCase';
import selectedColumnsReducer from './tableSearchReducer';

// Utils
import groupOptionsByOptGroup from '../../utils/optGroup/optGroup';

/**
 * @typedef {object} useTableSearchReturn
 * @property {Array<{lib: string, options: Array<ApiManagerTableFiltersSearchOptions>}>} searchOptionFiltered
 * @property {string} tableSearch
 * @property {(search: string) => void} setTableSearch
 * @property {string} searchColumnsGroups
 * @property {(search: string) => void} handleSearchColumnsGroups
 * @property {boolean} popoverSearchColumnsGroupsIsOpen
 * @property {(isOpen: boolean) => void} setPopoverSearchColumnsGroupsIsOpen
 * @property {Record<string, boolean>} selectedColumns - Internal state (object format for efficient lookups)
 * @property {string[]} selectedColumnsArray - Array format for API calls (derived from selectedColumns)
 * @property {(id: string) => void} handleSelectColumn
 * @property {(id: string) => void} handleDeselectColumn
 * @property {(ids: string[]) => void} handleSelectGroupColumns
 * @property {(ids: string[]) => boolean} areAllColumnsGroupSelected
 * @property {(options: Array<ApiManagerTableFiltersSearchOptions>) => void} handleSelectAllColumns
 * @property {(totalColumns: number) => boolean} areAllColumnsSelected
 * @property {(totalColumns: number) => boolean} isAllColumnsSelectionIndeterminate
 * @property {(ids: string[]) => boolean} isColumnsGroupSelectionIndeterminate
 * @property {(options:  Array<ApiManagerTableFiltersSearchOptions>) => number} tableColumnsCount
 */

/**
 * Use table search
 * @param {object} props - The props
 * @param {Array<ApiManagerTableFiltersSearchOptions>} props.searchOptions - The search options (from API search.options)
 * @param {string} [props.initialValue] - The initial search value (from API search.searchValue)
 * @param {Array<string>} [props.initialSelectedOptions] - The initial search selected options (from API search.selectedOptions)
 * @param {boolean} props.isLoading - The loading state
 * @returns {useTableSearchReturn} The table search
 */
const useTableSearch = ({
  searchOptions, initialValue, initialSelectedOptions, isLoading,
}) => {
  const [tableSearch, setTableSearch] = useState('');

  // Initialize reducer with object format converted from array (API format)
  const [selectedColumns, dispatchSelectedColumns] = useReducer(
    selectedColumnsReducer,
    convertArrayToObject(initialSelectedOptions || []),
  );

  /**
   * Initialize tableSearch from initialValue when data is loaded
   */
  useEffect(() => {
    if (!isLoading && initialValue !== undefined && initialValue !== tableSearch) {
      setTableSearch(initialValue);
    }
  }, [initialValue, isLoading]);

  /**
   * Reset selectedColumns when initialSelectedOptions changes and data is loaded
   */
  useEffect(() => {
    if (!isLoading && initialSelectedOptions !== undefined) {
      dispatchSelectedColumns({
        type: 'SELECT_MANY_COLUMNS',
        payload: { ids: initialSelectedOptions },
      });
    }
  }, [initialSelectedOptions, isLoading]);

  // Search
  const [searchColumnsGroups, setSearchColumnsGroups] = useState('');
  const [popoverSearchColumnsGroupsIsOpen, setPopoverSearchColumnsGroupsIsOpen] = useState(false);

  /**
   * Handle search columns groups
   * @param {string} search - The search
   */
  const handleSearchColumnsGroups = (search) => {
    setSearchColumnsGroups(search);
  };

  /**
   * Get the number of columns in the table
   * @param {Array<ApiManagerTableFiltersSearchOptions>} options - The search options
   * @returns {number}
   */
  const tableColumnsCount = (options) => ucTableColumnsCount(options);

  /**
   * Handle select column
   * @param {string} id - The column id
   * @returns {void}
   */
  const handleSelectColumn = (id) => {
    const isSelected = selectedColumns[id];
    dispatchSelectedColumns({
      type: isSelected ? 'DESELECT_COLUMN' : 'SELECT_COLUMN',
      payload: { id },
    });
  };

  /**
   * Handle deselect column
   * @param {string} id - The column id
   * @returns {void}
   */
  const handleDeselectColumn = (id) => {
    dispatchSelectedColumns({ type: 'DESELECT_COLUMN', payload: { id } });
  };

  /**
   * Handle select group columns
   * @param {string[]} ids - The column ids
   * @returns {void}
   */
  const handleSelectGroupColumns = (ids) => {
    const isAllSelected = ids.every((id) => selectedColumns[id]);
    dispatchSelectedColumns({
      type: isAllSelected ? 'DESELECT_MANY_COLUMNS' : 'SELECT_MANY_COLUMNS',
      payload: { ids },
    });
  };

  /**
   * Check if all columns are selected
   * @param {string[]} ids - The column ids
   * @returns {boolean}
   */
  const areAllColumnsGroupSelected = (ids) => ids.every((id) => selectedColumns[id]);

  /**
   * Check if all columns are selected
   * @param {number} totalColumns - The total number of columns
   * @returns {boolean}
   */
  const areAllColumnsSelected = (totalColumns) => ucAreAllColumnsSelected(selectedColumns, totalColumns);

  /**
   * @param {number} totalColumns
   * @returns {boolean}
   */
  const isAllColumnsSelectionIndeterminate = (totalColumns) => ucIsAllColumnsSelectionIndeterminate(
    selectedColumns,
    totalColumns,
  );

  /**
   * @param {string[]} ids
   * @returns {boolean}
   */
  const isColumnsGroupSelectionIndeterminate = (ids) => ucIsColumnsGroupSelectionIndeterminate(
    selectedColumns,
    ids,
  );

  /**
   * Handle select all columns
   * @param {Array<ApiManagerTableFiltersSearchOptions>} options - The search options
   * @returns {void}
   */
  const handleSelectAllColumns = (options) => {
    const count = tableColumnsCount(options);
    const ids = getAllColumnIds(options);
    dispatchSelectedColumns({
      type: count === Object.keys(selectedColumns).length
        ? 'DESELECT_MANY_COLUMNS' : 'SELECT_MANY_COLUMNS',
      payload: { ids },
    });
  };

  // /**
  //  * Handle search change
  //  * @param {React.ChangeEvent<HTMLInputElement>} e - The event
  //  * @returns {void}
  //  */
  // const onSearchChange = (e) => setTableSearch(e.target.value);
  // const handleTableSearch = useCallback(debounce(onSearchChange, 500), []);

  /**
   * Search in columns groups
   * @param {Array<ApiManagerTableFiltersSearchOptions>} options - The options
   * @param {string} search - The search
   * @returns {Array<ApiManagerTableFiltersSearchOptions>} The options
   */
  const searchInSearchOptions = (options, search) => ucSearchInSearchOptions(options, search);

  /**
   * @description The search columns filtered
   */
  const searchOptionFiltered = useMemo(
    () => {
      if ((searchOptions && !searchOptions.length) || isLoading) return [];
      const filteredOptions = searchInSearchOptions(searchOptions, searchColumnsGroups);
      return groupOptionsByOptGroup(filteredOptions);
    },
    [searchOptions, searchColumnsGroups, isLoading],
  );

  /**
   * Convert selectedColumns (object) to array format for API calls
   * This matches the API format: search.selectedOptions (Array<string>)
   */
  const selectedColumnsArray = useMemo(
    () => convertObjectToArray(selectedColumns),
    [selectedColumns],
  );

  return {
    searchOptionFiltered,
    tableSearch,
    setTableSearch,
    // handleTableSearch,

    // Search
    searchColumnsGroups,
    handleSearchColumnsGroups,
    popoverSearchColumnsGroupsIsOpen,
    setPopoverSearchColumnsGroupsIsOpen,

    // Selected columns
    selectedColumns, // Object format for internal use
    selectedColumnsArray, // Array format for API calls (matches search.selectedOptions)
    handleSelectColumn,
    handleDeselectColumn,
    handleSelectGroupColumns,
    areAllColumnsGroupSelected,
    handleSelectAllColumns,
    areAllColumnsSelected,
    isAllColumnsSelectionIndeterminate,
    isColumnsGroupSelectionIndeterminate,
    tableColumnsCount,
  };
};

export default useTableSearch;
